import type { FarmOrigin } from "@/lib/catalog/types";
import type { Locale } from "@/lib/i18n/getLocale";
import { isOwnProduction } from "@/lib/seo/jsonld";

// Step 10.5 commit 11 v2.1 / Task B - Option E (locked Jun 15):
// concise own-production badge rendered bottom-right of every variety
// card. Mountain icon (teal) + altitude (dark). Partner-grown cards
// carry no badge - cleaner card surface, badge serves as the
// own-production differentiator.
//
// Replaces the verbose "OUR BRANDED ROSES · GROWN AT 3,200M" pill
// shipped in Step 8.5 Half B. The `label` + `strings.fmBannerLine`
// prop path is preserved as a no-op so existing call-sites compile
// unchanged; the value is ignored and the standard "{altitude}m"
// format renders instead.

// MKT-#93: "3,200m" (EN) / "3.200m" (ES). Not toLocaleString("es-ES"):
// Spanish CLDR skips the separator on 4-digit numbers ("3200"), and the
// result must match between server render and every browser.
export function formatAltitudeMeters(meters: number, locale: Locale): string {
  const en = Math.round(meters).toLocaleString("en-US");
  return `${locale === "es" ? en.replace(/,/g, ".") : en}m`;
}

export function FmBanner({
  farm,
  variant = "card",
  locale = "en",
}: {
  farm: FarmOrigin;
  variant?: "card" | "detail";
  // Step 10.5 commit 11 v2.1: label prop accepted for back-compat
  // with existing callers but ignored. Option E format is locked.
  label?: string;
  // MKT-#93: locale-aware number format. ES uses "3.200m" (period
  // thousands separator + Spanish aria), not the US "3,200m".
  locale?: Locale;
}) {
  if (!isOwnProduction(farm.name)) return null;
  const altitude = formatAltitudeMeters(farm.altitudeMeters, locale);
  const ariaLabel = locale === "es" ? `Cultivada a ${altitude}` : `Grown at ${altitude}`;
  // Step 10.5 commit 11 v2.2 / Issue 3: bumped 12px -> 16px so the
  // pill doesn't crowd the photo's bottom-right corner. Matches the
  // breathing-room spec Alexis flagged on the v2.1 walkthrough.
  const positionClass =
    variant === "card"
      ? "absolute bottom-4 right-4 z-[2]"
      : "";
  return (
    <span
      data-testid="fm-banner"
      data-variant={variant}
      aria-label={ariaLabel}
      style={{
        backgroundColor: "rgba(255,255,255,0.95)",
        color: "#1A1A1A",
        fontSize: "10px",
        letterSpacing: "0.04em",
        fontWeight: 500,
        padding: "5px 10px",
        borderRadius: "999px",
        gap: "5px",
      }}
      className={`inline-flex items-center backdrop-blur-sm shadow-sm ${positionClass}`}
    >
      <MountainIcon />
      {altitude}
    </span>
  );
}

function MountainIcon() {
  // Tabler-style mountain icon (ti-mountain). 13px, brand teal.
  return (
    <svg
      aria-hidden
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#5BC5BA"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 20l6.5-9.5 4 5 3-4L21 20H3z" />
      <circle cx="9.5" cy="10.5" r="0.5" fill="#5BC5BA" stroke="none" />
    </svg>
  );
}
